import { ChangeDetectionStrategy, Component, Input, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { fadeInOutAnimation } from '@newroom-connect/library/animations';
import { IFileProcessingProgress } from '@newroom-connect/library/types';
import { FileProcessingService, LoggingService } from '@newroom-connect/library/services';

import { IconComponent } from '../icon/icon.component';

import { DownloadProgressComponent } from './progress-display/download-progress/download-progress.component';
import { GenericProgressComponent } from './progress-display/generic-progress/generic-progress.component';
import { PdfExportProgressComponent } from './progress-display/pdf-export-progress/pdf-export-progress.component';
import { TranslationProgressComponent } from './progress-display/translation-progress/translation-progress.component';

@Component({
  selector: 'nrc-file-processing-status',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconComponent,
    PdfExportProgressComponent,
    DownloadProgressComponent,
    TranslationProgressComponent,
    GenericProgressComponent
  ],
  templateUrl: './file-processing-status.component.html',
  animations: [fadeInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileProcessingStatusComponent implements OnInit, OnDestroy {
  private readonly fileProcessingService = inject(FileProcessingService);
  private readonly logger = inject(LoggingService);
  private readonly destroy$ = new Subject<void>();
  private autoCloseTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  @Input() public set processingProgress(value: IFileProcessingProgress[] | null) {
    this.processingProgressSignal.set(value);
  }

  @Input() public autoCloseDuration = 5000; // Auto-close completed items after 5 seconds by default

  public readonly processingProgressSignal = signal<IFileProcessingProgress[] | null>(null);

  /**
   *
   */
  public ngOnInit(): void {
    this.fileProcessingService.processingProgress
      .pipe(takeUntil(this.destroy$))
      .subscribe((progress: IFileProcessingProgress[] | null) => {
        this.processingProgressSignal.set(progress);

        // Schedule auto-close for completed or failed items
        if (progress) {
          progress.forEach(item => {
            if (item.status === 'COMPLETED' || item.status === 'FAILED') {
              this.scheduleAutoClose(item.id);
            }
          });
        }
      });
  }

  /**
   *
   */
  public ngOnDestroy(): void {
    // Clear all auto-close timeouts
    this.autoCloseTimeouts.forEach(timeout => clearTimeout(timeout));
    this.autoCloseTimeouts.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Remove a specific progress item.
   *
   * @param itemId ID of the item to remove.
   */
  public removeItem(itemId: string): void {
    const currentProgress = this.processingProgressSignal();

    if (!currentProgress) {
      return;
    }

    // Clear timeout if exists
    const timeout = this.autoCloseTimeouts.get(itemId);

    if (timeout) {
      clearTimeout(timeout);
      this.autoCloseTimeouts.delete(itemId);
    }

    // Remove item from progress
    const updatedProgress = currentProgress.filter(item => item.id !== itemId);

    this.processingProgressSignal.set(updatedProgress.length > 0 ? updatedProgress : null);
  }

  /**
   * Schedule auto-close for completed or failed items.
   *
   * @param itemId ID of the item to auto-close.
   */
  private scheduleAutoClose(itemId: string): void {
    // Clear existing timeout if any
    const existingTimeout = this.autoCloseTimeouts.get(itemId);

    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Schedule new timeout
    const timeout = setTimeout(() => {
      this.removeItem(itemId);
      this.autoCloseTimeouts.delete(itemId);
    }, this.autoCloseDuration);

    this.autoCloseTimeouts.set(itemId, timeout);
  }
}
